/**
 * Bridge to the Eitaa native client's secure-window mode.
 *
 * `enableWindowSecure()` asks the host app to apply Android's FLAG_SECURE,
 * which blocks OS screenshots and screen recording. Per the Eitaa JS SDK this
 * is Android-only, v1.5+ — no other client can enforce it, so protected
 * content is withheld there instead of shown unprotected.
 */
const MIN_SECURE_VERSION = "1.5";

export type WindowSecureSupport =
  /** Android client new enough to honour secure-window mode. */
  | "supported"
  /** Android, but the installed Eitaa is older than v1.5 — the host ignores the event. */
  | "outdated"
  /** A native client that cannot enforce it (iOS, desktop). */
  | "unsupported-platform"
  /** No native host at all (plain browser / dev server). */
  | "unavailable";

const getWebApp = () =>
  typeof window === "undefined" ? undefined : window.Eitaa?.WebApp;

export const getWindowSecureSupport = (): WindowSecureSupport => {
  const webApp = getWebApp();
  // Normalized because `platform` is passed through verbatim from the launch
  // URL; a casing difference must not flip a phone into the wrong branch.
  const platform = (webApp?.platform ?? "").trim().toLowerCase();

  if (!webApp || !platform || platform === "unknown") return "unavailable";
  if (platform !== "android") return "unsupported-platform";
  if (!webApp.enableWindowSecure) return "outdated";
  return webApp.isVersionAtLeast?.(MIN_SECURE_VERSION) ? "supported" : "outdated";
};

/**
 * Ask the host to block captures. Returns a callback that releases the
 * request; a no-op when the host cannot honour it.
 */
export const requestWindowSecure = (): (() => void) => {
  if (getWindowSecureSupport() !== "supported") return () => {};

  const webApp = getWebApp();
  try {
    webApp?.enableWindowSecure?.();
  } catch {
    // A host that rejects the event must not take the page down with it.
    return () => {};
  }

  return () => {
    try {
      webApp?.disableWindowSecure?.();
    } catch {
      // Best-effort release; worst case the window stays secure until the
      // mini-app closes, which fails safe.
    }
  };
};

/** Close the mini-app. No-op outside the Eitaa host. */
export const closeMiniApp = (): void => {
  try {
    getWebApp()?.close();
  } catch {
    // Nothing useful to do — the blocking notice stays on screen either way.
  }
};
